import { type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import type { PricingRange } from '@/hooks/use-pricing-ranges';
import { useDeleteRange } from '../hooks/use-pricing-range-mutations';
import { PricingRangeCard } from './pricing-range-card';
import { RangeFormModal, type RangeFormMode } from './range-form-modal';
import { RangeHistoryModal } from './range-history-modal';

export function PageHeader({ count }: { count: number }): ReactNode {
  return (
    <div>
      <h3 className="mb-sm text-xs font-semibold uppercase text-text-tertiary">
        Pricing por rangos
      </h3>
      <p className="text-xs text-text-tertiary">
        <span className="font-semibold tabular-nums text-text-primary">
          {count.toLocaleString('es-ES')}
        </span>{' '}
        creadores en el sistema
      </p>
    </div>
  );
}

export type RangeListProps = {
  ranges: PricingRange[];
  onEdit: (r: PricingRange) => void;
  onHistory: (id: string) => void;
  onDelete: (r: PricingRange) => void;
};

// Responsive auto-fill grid: as many columns as fit at minmax(300px, 1fr).
// Cards reflow naturally based on available width — 1 col on narrow,
// 2-4 cols on wider viewports.
const RANGE_GRID =
  'grid gap-md grid-cols-[repeat(auto-fill,minmax(300px,1fr))]';

export function RangeList(p: RangeListProps): ReactNode {
  return (
    <div className={RANGE_GRID}>
      {p.ranges.map((range) => (
        <PricingRangeCard
          key={range.id}
          range={range}
          onEdit={() => p.onEdit(range)}
          onHistory={() => p.onHistory(range.id)}
          onDelete={() => p.onDelete(range)}
        />
      ))}
    </div>
  );
}

export function useDeleteHandler(): (r: PricingRange) => void {
  const del = useDeleteRange();
  return (range) => {
    const label = range.range_label || `${range.range_min}+`;
    if (window.confirm(`¿Eliminar el rango "${label}"?`)) del.mutate(range.id);
  };
}

export function AddRangeButton({
  onClick,
}: {
  onClick: () => void;
}): ReactNode {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 text-xs text-text-secondary transition-colors hover:text-brand-red"
    >
      <Plus size={14} />
      Añadir rango
    </button>
  );
}

export type ModalsProps = {
  formMode: RangeFormMode | null;
  setFormMode: (m: RangeFormMode | null) => void;
  historyId: string | null;
  setHistoryId: (id: string | null) => void;
};

export function RangeModals(p: ModalsProps): ReactNode {
  return (
    <>
      <RangeFormModal mode={p.formMode} onClose={() => p.setFormMode(null)} />
      <RangeHistoryModal
        rangeId={p.historyId}
        onClose={() => p.setHistoryId(null)}
      />
    </>
  );
}
